import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";

export function StodlinjenInfoPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const title = isSv
    ? "Stödlinjen — gratis hjälp vid spelproblem"
    : "Stödlinjen — free help for gambling problems";
  const desc = isSv
    ? "Stödlinjen är en kostnadsfri och anonym stödtjänst för dig som spelar för mycket eller är anhörig."
    : "Stödlinjen is a free, anonymous support service for problem gamblers and their families.";

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: "Stödlinjen" }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <section className="mt-6 grid gap-4 md:grid-cols-3">
        <div className="card p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">
            {isSv ? "Telefon" : "Phone"}
          </p>
          <p className="mt-2 text-2xl font-bold text-ink-900">020-81 91 00</p>
          <p className="mt-1 text-sm text-ink-600">
            {isSv ? "Gratis och anonymt" : "Free and anonymous"}
          </p>
        </div>
        <div className="card p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">
            Chatt
          </p>
          <p className="mt-2 text-lg font-bold text-ink-900">stodlinjen.se</p>
          <p className="mt-1 text-sm text-ink-600">
            {isSv ? "Mån–fre, eftermiddagar och kvällar" : "Mon–Fri, afternoons and evenings"}
          </p>
        </div>
        <div className="card p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">
            E-post
          </p>
          <p className="mt-2 text-lg font-bold text-ink-900">stodlinjen@ki.se</p>
          <p className="mt-1 text-sm text-ink-600">
            {isSv ? "Svar inom några dagar" : "Reply within a few days"}
          </p>
        </div>
      </section>

      <article className="prose prose-ink mt-10 max-w-3xl [&>h2]:mt-10 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3">
        <h2>{isSv ? "Vad är Stödlinjen?" : "What is Stödlinjen?"}</h2>
        <p>
          {isSv
            ? "Stödlinjen drivs av Centrum för psykiatriforskning vid Karolinska Institutet på uppdrag av Folkhälsomyndigheten. Tjänsten är gratis, anonym och bemannad av professionella rådgivare. Du kan ringa, chatta eller mejla — du behöver inte uppge ditt namn eller personnummer för att få hjälp."
            : "Stödlinjen is run by the Centre for Psychiatry Research at Karolinska Institutet, on behalf of the Public Health Agency of Sweden. The service is free, anonymous and staffed by professional counsellors. You can call, chat or email — you do not need to give your name or social security number to get help."}
        </p>

        <h2>{isSv ? "När ska du höra av dig?" : "When should you reach out?"}</h2>
        <p>
          {isSv
            ? "Du behöver inte vara ”beroende” för att kontakta Stödlinjen. Många hör av sig efter en enstaka händelse där spelandet kändes ur kontroll. Andra hör av sig som anhöriga som är oroliga för en familjemedlem eller partner. Stödlinjen ger råd om allt från ekonomisk planering till hur en samtalsstart med en närstående kan se ut."
            : "You do not need to be ”addicted” to contact Stödlinjen. Many people reach out after a single incident where gambling felt out of control. Others reach out as concerned family members or partners. Stödlinjen provides guidance on everything from financial planning to how to open a conversation with someone close to you."}
        </p>

        <h2>{isSv ? "Tecken på problem" : "Signs of trouble"}</h2>
        <p>
          {isSv
            ? "Tre frågor är ett bra första test. Spelar du för pengar du egentligen inte har råd att förlora? Har du försökt minska eller sluta utan att lyckas? Har du ljugit för någon nära dig om hur mycket du spelar eller förlorat? Svarar du ja på en eller flera är det värt att ringa Stödlinjen — det kostar inget och tar bara några minuter."
            : "Three questions are a good first test. Are you gambling money you cannot afford to lose? Have you tried to cut down or stop without success? Have you lied to someone close to you about how much you gamble or lose? If you answer yes to one or more, it is worth calling Stödlinjen — it costs nothing and takes only minutes."}
        </p>

        <h2>{isSv ? "Andra resurser" : "Other resources"}</h2>
        <p>
          {isSv
            ? "Spelpaus.se låter dig stänga av dig från alla svenskt licensierade spelbolag. Många regioner har specialistmottagningar för spelberoende — be din vårdcentral om en remiss. Anonyma Spelare och Spelare Anonyma är fristående självhjälpsgrupper med fysiska och digitala möten."
            : "Spelpaus.se lets you self-exclude from every Swedish-licensed operator. Many regions have specialist outpatient clinics for problem gambling — ask your primary care for a referral. Gamblers Anonymous holds independent self-help meetings, both in person and online."}
        </p>
      </article>

      <InternalLinks context="responsible" />
    </div>
  );
}
